import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { formatPrice, readApiError } from "@/lib/format";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  bonsai: "Bonsai",
  succulents: "Succulents",
  terrariums: "Terrariums",
  accessories: "Accessories",
};

const CATEGORY_HEROES = {
  bonsai:
    "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/00d67fa453e54f87dc8e7ba6a506c118fd4f879f422c6afacb81c5ad090e443d.png",
  succulents:
    "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/568cbe699f9af336ffafe55a4e5518bcfaefc8af86651c848e1931c83ceb55a5.png",
  terrariums:
    "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/613610d7b76282e2b9b10648398814787ab2ada89968bf6ac1bb9ecea862a116.png",
  accessories:
    "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/8e64cdbaf5016fb439bca7cd264c4c6da34109affb108422467c440fe210023e.png",
};

// Phase 1.6: editorial banner above the "All" grid
const ALL_BANNER =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/bf1983370910825bb331594325c1ab1389c2cb6a5091e2ab84ef93d01211966c.png";

const CATEGORY_TAGLINES = {
  bonsai: "Trees with weather in them. Trained slowly, shipped honestly.",
  succulents:
    "Small, hardy, and almost meditative. The first plant most of us keep alive.",
  terrariums: "Tiny glass weather systems. Coastal moss, driftwood, and patience.",
  accessories: "The honest tools — brass, ceramic, coastal stones.",
};

function ProductCard({ p }) {
  return (
    <Link
      to={`/shop/product/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group block overflow-hidden rounded-2xl border border-[#5E594E] bg-[#4D4940] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C9A961]/60 hover:shadow-[0_8px_24px_rgba(201,169,97,0.15)]"
    >
      <div className="overflow-hidden">
        <img
          src={p.image_url}
          alt={p.name}
          className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
        />
      </div>
      <div className="space-y-1 p-5">
        <p className="text-xs uppercase tracking-wider text-[#C9A961]">
          {CATEGORY_LABELS[p.category] || p.category}
        </p>
        <h3 className="font-serif text-xl leading-tight tracking-wide text-[#F5EFE2]">
          {p.name}
        </h3>
        <p className="pt-1 font-serif text-lg text-[#F5EFE2]">
          {formatPrice(p.price_cents)}
        </p>
      </div>
    </Link>
  );
}

export default function Shop() {
  const { category } = useParams();
  const cat = category ? category.toLowerCase() : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = cat ? { category: cat } : {};
        const res = await api.get(`/products`, { params });
        if (!cancelled) setItems(res.data.products || []);
      } catch (e) {
        if (!cancelled)
          toast.error("Could not load catalog", {
            description: readApiError(e),
          });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cat]);

  const heroImage = cat ? CATEGORY_HEROES[cat] : ALL_BANNER;
  const heroLabel = cat ? CATEGORY_LABELS[cat] || cat : "The Collection";
  const tagline = cat
    ? CATEGORY_TAGLINES[cat] ||
      "Hand-shaped on the coast. Take what calls you."
    : "Shaped by hand on the Pacific Coast.";
  const eyebrow = cat ? `Shop · ${heroLabel}` : "The Collection";

  return (
    <PublicLayout overlayNav padTop={false}>
      <section
        data-testid="shop-hero"
        className="relative isolate flex min-h-[60vh] items-end overflow-hidden"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={heroImage}
            alt={heroLabel}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#3D3A33]/40 via-[#3D3A33]/55 to-[#3D3A33]/90" />
        </div>
        <div className="mx-auto w-full max-w-6xl px-6 pb-16 pt-32 text-[#F5EFE2]">
          <p className="text-xs uppercase tracking-[0.28em] text-[#C9A961]">
            {eyebrow}
          </p>
          <h1
            data-testid="shop-headline"
            className="mt-3 font-serif text-5xl tracking-wide sm:text-6xl"
          >
            {cat ? heroLabel : "The Collection —"}
            {!cat && (
              <>
                <br />
                <span className="text-[#C9A961]">Shaped by Hand on the Pacific Coast.</span>
              </>
            )}
          </h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-[#F5EFE2]/85">
            {tagline}
          </p>
        </div>
      </section>

      {/* CATEGORY TABS */}
      <nav
        data-testid="shop-tabs"
        className="border-b border-[#5E594E]/70 bg-[#3D3A33]"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap gap-2 px-6 py-4">
          {[
            { slug: "", label: "All" },
            { slug: "bonsai", label: "Bonsai" },
            { slug: "succulents", label: "Succulents" },
            { slug: "terrariums", label: "Terrariums" },
            { slug: "accessories", label: "Accessories" },
          ].map((t) => {
            const active = (cat || "") === t.slug;
            return (
              <Link
                key={t.slug || "all"}
                to={t.slug ? `/shop/${t.slug}` : "/shop"}
                data-testid={`shop-tab-${t.slug || "all"}`}
                className={[
                  "rounded-full px-4 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-[#C9A961] text-[#3D3A33]"
                    : "border border-[#5E594E] bg-[#4D4940] text-[#F5EFE2]/85 hover:border-[#C9A961]/50 hover:text-[#C9A961]",
                ].join(" ")}
              >
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* GRID */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        {loading ? (
          <p
            data-testid="shop-loading"
            className="py-20 text-center text-[#C9BEA8]"
          >
            Loading the workbench…
          </p>
        ) : items.length === 0 ? (
          <p
            data-testid="shop-empty"
            className="py-20 text-center text-[#C9BEA8]"
          >
            Nothing here yet. New things arrive every few weeks.
          </p>
        ) : (
          <div
            data-testid="shop-grid"
            className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            {items.map((p) => (
              <ProductCard key={p.slug} p={p} />
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
